import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Grid, Paper, Typography } from "@material-ui/core";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import themeColorsDesingSystem from "assets/theme/ds-colors";
import MaskAndValid from "utils/maskAndValid";
import ModalComponent from "../../criar-vitrine/incluir-produtos/components/modal";
import groupPrePedidoItens from "../utils/groupPrePedidoItens";

const DS = themeColorsDesingSystem;

function formatDate(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function formatVigencia(inicio, fim) {
  if (!inicio && !fim) return "—";
  if (inicio && fim) return `${inicio} - ${fim}`;
  return inicio || fim;
}

function MetaField({ label, value }) {
  return (
    <Box className="pre-pedido-detail__meta-field">
      <Typography variant="caption" className="pre-pedido-detail__meta-label">
        {label}
      </Typography>
      <Typography variant="body2" className="pre-pedido-detail__meta-value">
        {value}
      </Typography>
    </Box>
  );
}

export default function PrePedidoDetailDrawer({
  open,
  onClose,
  detalhe,
  loading = false,
}) {
  const [expandedSections, setExpandedSections] = useState(new Set());

  useEffect(() => {
    if (!open) setExpandedSections(new Set());
  }, [open, detalhe]);

  const grouped = useMemo(
    () => groupPrePedidoItens(detalhe?.Itens || detalhe?.itens),
    [detalhe],
  );

  const itens = detalhe?.Itens || detalhe?.itens || [];
  const valorTotal = Number(detalhe?.ValorTotal ?? detalhe?.valorTotal) || 0;
  const valorMin = Number(
    detalhe?.ValorMinimoVitrine ?? detalhe?.valorMinimoVitrine,
  );
  const qtdMin = Number(
    detalhe?.QuantidadeMinimaPedido ?? detalhe?.quantidadeMinimaPedido,
  );
  const qtdTotal = itens.reduce(
    (acc, i) => acc + Number(i.Quantidade ?? i.quantidade ?? 0),
    0,
  );
  const valOk = !valorMin || valorMin <= 0 || valorTotal >= valorMin;
  const qtyOk = !qtdMin || qtdMin <= 0 || qtdTotal >= qtdMin;

  const toggleSection = (id) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const nomeVitrine =
    detalhe?.NomeVitrine || detalhe?.nomeVitrine || "Vitrine B2B";
  const codigoVitrine = detalhe?.IdVitrineVirtualB2b ?? detalhe?.idVitrineVirtualB2b;
  const status = detalhe?.Status || detalhe?.status || "Criado";

  return (
    <ModalComponent
      open={open}
      onClose={onClose}
      width="720px"
      title="Detalhe do pré-pedido"
    >
      {loading ? (
        <Box className="pre-pedido-detail__state">
          <CircularProgress size={36} style={{ color: DS.primary.main }} />
          <Typography variant="body2" color="textSecondary">
            Carregando detalhes…
          </Typography>
        </Box>
      ) : !detalhe ? (
        <Box className="pre-pedido-detail__state">
          <Typography variant="body2" color="textSecondary">
            Selecione um pré-pedido para ver os detalhes.
          </Typography>
        </Box>
      ) : (
        <Box className="pre-pedido-detail">
          <Typography
            variant="subtitle2"
            className="pre-pedido-detail__vitrine-name"
          >
            {nomeVitrine}
          </Typography>

          <Grid container spacing={2} className="pre-pedido-detail__meta-grid">
            <Grid item xs={12} sm={6}>
              <MetaField
                label="Código da vitrine"
                value={codigoVitrine ?? "—"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetaField
                label="Consumidor"
                value={detalhe.RazaoSocialLoja || detalhe.razaoSocialLoja || "—"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetaField
                label="CNPJ"
                value={
                  MaskAndValid.maskCNPJ(
                    detalhe.CnpjLoja || detalhe.cnpjLoja || "",
                  ) || "—"
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetaField
                label="Data do pré-pedido"
                value={formatDate(
                  detalhe.DataPrePedido || detalhe.dataPrePedido,
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetaField
                label="Vigência"
                value={formatVigencia(
                  detalhe.VigenciaInicioFormat || detalhe.vigenciaInicioFormat,
                  detalhe.VigenciaFimFormat || detalhe.vigenciaFimFormat,
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetaField label="Status" value={status} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetaField
                label="Condição de pagamento"
                value={detalhe.CondicaoPagamento || detalhe.condicaoPagamento || "—"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetaField label="Quantidade total" value={`${qtdTotal} un.`} />
            </Grid>
          </Grid>

          <Paper
            variant="outlined"
            className="pre-pedido-detail__summary"
            elevation={0}
          >
            <Box className="pre-pedido-detail__summary-row">
              <Typography className="pre-pedido-detail__vitrine-label">
                Vitrine
              </Typography>
              <Box className="pre-pedido-detail__summary-right">
                {valorMin > 0 && (
                  <span
                    className={`pre-pedido-detail__badge ${
                      valOk
                        ? "pre-pedido-detail__badge--ok"
                        : "pre-pedido-detail__badge--err"
                    }`}
                  >
                    Mín. R$ {MaskAndValid.maskPrice(valorMin)}
                  </span>
                )}
                {qtdMin > 0 && (
                  <span
                    className={`pre-pedido-detail__badge ${
                      qtyOk
                        ? "pre-pedido-detail__badge--ok"
                        : "pre-pedido-detail__badge--err"
                    }`}
                  >
                    Mín. {qtdMin} un
                  </span>
                )}
                <Typography className="pre-pedido-detail__total">
                  R$ {MaskAndValid.maskPrice(valorTotal)}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            className="pre-pedido-detail__catalog"
            elevation={0}
          >
            <Box className="pre-pedido-detail__catalog-inner custom-scroll">
              {Object.keys(grouped)
                .sort()
                .map((catName) => {
                  const subcats = grouped[catName];
                  let catQty = 0;
                  let catVal = 0;
                  Object.values(subcats).forEach((lines) => {
                    lines.forEach((line) => {
                      catQty +=
                        Number(line.Quantidade ?? line.quantidade) || 0;
                      catVal +=
                        Number(line.ValorTotalLinha ?? line.valorTotalLinha) ||
                        0;
                    });
                  });

                  return (
                    <Box key={catName} className="pre-pedido-detail__category">
                      <Box className="pre-pedido-detail__category-head">
                        <Typography
                          className="pre-pedido-detail__category-name"
                          style={{ color: DS.primary.main }}
                        >
                          {catName}
                        </Typography>
                        <Typography className="pre-pedido-detail__category-sum">
                          R$ {MaskAndValid.maskPrice(catVal)} em {catQty} itens
                        </Typography>
                      </Box>

                      {Object.keys(subcats)
                        .sort()
                        .map((subName) => {
                          const lines = subcats[subName];
                          let subQty = 0;
                          let subVal = 0;
                          lines.forEach((line) => {
                            subQty +=
                              Number(line.Quantidade ?? line.quantidade) || 0;
                            subVal +=
                              Number(
                                line.ValorTotalLinha ?? line.valorTotalLinha,
                              ) || 0;
                          });
                          const accordId = `${catName}-${subName}`.replace(
                            /\s+/g,
                            "-",
                          );
                          const isExpanded = expandedSections.has(accordId);

                          return (
                            <Box
                              key={subName}
                              className="pre-pedido-detail__sub"
                            >
                              <Box
                                className="pre-pedido-detail__accordion"
                                onClick={() => toggleSection(accordId)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    toggleSection(accordId);
                                  }
                                }}
                              >
                                <Typography className="pre-pedido-detail__sub-name">
                                  {subName}
                                </Typography>
                                <Typography
                                  component="span"
                                  className="pre-pedido-detail__sub-pill"
                                >
                                  R$ {MaskAndValid.maskPrice(subVal)} em{" "}
                                  {subQty} itens
                                </Typography>
                                {isExpanded ? (
                                  <ExpandLessIcon
                                    style={{ width: 20, height: 20, color: DS.gray.dark }}
                                  />
                                ) : (
                                  <ExpandMoreIcon
                                    style={{ width: 20, height: 20, color: DS.gray.dark }}
                                  />
                                )}
                              </Box>

                              {isExpanded && (
                                <Box className="pre-pedido-detail__lines">
                                  {lines.map((line, idx) => {
                                    const nome =
                                      line.NomeProduto ||
                                      line.nomeProduto ||
                                      line.IdProduto ||
                                      line.idProduto;
                                    const qtd =
                                      Number(
                                        line.Quantidade ?? line.quantidade,
                                      ) || 0;
                                    const unit =
                                      Number(
                                        line.ValorUnitario ?? line.valorUnitario,
                                      ) || 0;
                                    const bruto =
                                      Number(
                                        line.ValorUnitarioBruto ??
                                          line.valorUnitarioBruto,
                                      ) || 0;
                                    const total =
                                      Number(
                                        line.ValorTotalLinha ??
                                          line.valorTotalLinha,
                                      ) || unit * qtd;
                                    const cond =
                                      line.CondicaoPagamento ||
                                      line.condicaoPagamento;

                                    return (
                                      <Box
                                        key={`${line.IdProduto || line.idProduto}-${idx}`}
                                        className="pre-pedido-detail__line"
                                      >
                                        <Box className="pre-pedido-detail__line-main">
                                          <Typography className="pre-pedido-detail__line-name">
                                            {nome}
                                          </Typography>
                                          {cond ? (
                                            <Typography className="pre-pedido-detail__line-cond">
                                              Condição: {cond}
                                            </Typography>
                                          ) : null}
                                        </Box>
                                        <Box className="pre-pedido-detail__line-prices">
                                          {bruto > unit && (
                                            <Typography className="pre-pedido-detail__line-gross">
                                              R$ {MaskAndValid.maskPrice(bruto)}
                                            </Typography>
                                          )}
                                          <Typography className="pre-pedido-detail__line-unit">
                                            R$ {MaskAndValid.maskPrice(unit)}
                                          </Typography>
                                          <Typography className="pre-pedido-detail__line-qty">
                                            Qtd: {qtd}
                                          </Typography>
                                          <Typography
                                            className="pre-pedido-detail__line-total"
                                            style={{ color: DS.primary.main }}
                                          >
                                            R$ {MaskAndValid.maskPrice(total)}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                    </Box>
                  );
                })}
            </Box>
          </Paper>

          <Box
            className="pre-pedido-detail__footer"
            style={{ borderTopColor: DS.stone.main }}
          >
            <Typography
              variant="caption"
              style={{ color: DS.gray.dark, fontWeight: 600 }}
            >
              Valor total do pré-pedido
            </Typography>
            <Typography
              variant="h5"
              style={{
                color: DS.primary.main,
                fontWeight: 700,
                margin: 0,
              }}
            >
              R$ {MaskAndValid.maskPrice(valorTotal)}
            </Typography>
          </Box>
        </Box>
      )}
    </ModalComponent>
  );
}
